import { FiArrowLeft, FiArrowUpRight, FiCheck } from 'react-icons/fi';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import NotFoundPage from '../components/NotFoundPage';
import { fetchProject } from '../api/projects';
import { getProjectBySlug } from './projects';
import styles from './Portfolio.module.css';

const ArchitectureDiagram = ({ nodes }) => (
  <div className={styles.architectureDiagram}>
    {(nodes || []).map((node, index) => (
      <div className={styles.architectureStep} key={node}>
        <div>
          <span>{String(index + 1).padStart(2, '0')}</span>
          <strong>{node}</strong>
        </div>
        {index < nodes.length - 1 && <i aria-hidden="true">→</i>}
      </div>
    ))}
  </div>
);

const ProjectDetailPage = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage?.startsWith('zh') ? 'zh' : 'en';
  const { slug } = useParams();
  const [project, setProject] = useState(() => getProjectBySlug(slug, language));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchProject(slug, language)
      .then((item) => { if (active) setProject(item); })
      .catch(() => { if (active) setProject(getProjectBySlug(slug, language)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug, language]);

  if (loading && !project) return <div className={styles.pageWrap}>{t('projectDetail.loading')}</div>;
  if (!project) return <NotFoundPage />;

  return (
    <div className={styles.projectDetail}>
      <Link to="/projects" className={styles.backLink}>
        <FiArrowLeft /> {t('projectDetail.allProjects')}
      </Link>

      {project.contentLanguage && project.contentLanguage !== language && (
        <div className={styles.languageNotice}>{t('projects.translationMissing')}</div>
      )}

      <header className={styles.projectDetailHeader}>
        <div>
          <p className={styles.eyebrow}>{project.label} · {project.year}</p>
          <h1>{project.name}</h1>
          <p>{project.summary}</p>
        </div>
        <div className={styles.projectLinks}>
          {project.githubUrl && (
            <a href={project.githubUrl} target="_blank" rel="noreferrer">GitHub <FiArrowUpRight /></a>
          )}
          {project.demoUrl && (
            <a href={project.demoUrl} target="_blank" rel="noreferrer">{t('projectDetail.liveSite')} <FiArrowUpRight /></a>
          )}
        </div>
      </header>

      <div className={`${styles.projectVisual} ${styles.projectHeroVisual}`}>
        <span className={styles.projectVisualLabel}>{t('projectDetail.architecture')}</span>
        <ArchitectureDiagram nodes={project.architecture} />
      </div>

      <section className={styles.detailGrid}>
        <div>
          <p className={styles.eyebrow}>{t('projectDetail.overview')}</p>
          <h2>{t('projectDetail.whatItIs')}</h2>
        </div>
        <div className={styles.detailCopy}>
          <p>{project.overview}</p>
          <div className={styles.tagList}>
            {(project.technologies || []).map((technology) => <span key={technology}>{technology}</span>)}
          </div>
        </div>
      </section>

      <section className={styles.detailGrid}>
        <div>
          <p className={styles.eyebrow}>{t('projectDetail.features')}</p>
          <h2>{t('projectDetail.whatMatters')}</h2>
        </div>
        <ul className={styles.highlightList}>
          {(project.highlights || []).map((highlight) => (
            <li key={highlight}><FiCheck /> <span>{highlight}</span></li>
          ))}
        </ul>
      </section>

      <section className={styles.detailSection}>
        <div className={styles.sectionHeading}>
          <div>
            <p className={styles.eyebrow}>{t('projectDetail.decisions')}</p>
            <h2>{t('projectDetail.decisionsTitle')}</h2>
          </div>
        </div>
        <div className={styles.decisionGrid}>
          {(project.decisions || []).map((decision, index) => (
            <article key={decision.title}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <h3>{decision.title}</h3>
              <p>{decision.text}</p>
            </article>
          ))}
        </div>
      </section>

      {project.screenshots?.length > 0 && (
        <section className={styles.detailSection}>
          <div className={styles.sectionHeading}>
            <div>
              <p className={styles.eyebrow}>{t('projectDetail.gallery')}</p>
              <h2>{t('projectDetail.galleryTitle')}</h2>
            </div>
          </div>
          <div className={styles.screenshotGrid}>
            {project.screenshots.map((screenshot, index) => (
              <figure key={screenshot.src}>
                <img src={screenshot.src} alt={screenshot.alt || `${project.name} screenshot ${index + 1}`} loading="lazy" />
                {screenshot.caption && <figcaption>{screenshot.caption}</figcaption>}
              </figure>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default ProjectDetailPage;
